import { memo, useCallback, useEffect } from 'react'
import type { CSSProperties, MouseEvent } from 'react'
import './ContextMenu.css'

export interface ContextMenuAction {
  label: string
  onClick: () => void
  disabled?: boolean
  danger?: boolean
}

type MenuPosition = CSSProperties & {
  '--context-menu-left': string
  '--context-menu-top': string
}

export function isEditableKeyboardTarget(target: EventTarget | null) {
  const element = target instanceof HTMLElement ? target : null
  if (!element) return false
  return element.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName)
}

const ContextMenuItem = memo(function ContextMenuItem({ action, onClose }: {
  action: ContextMenuAction
  onClose: () => void
}) {
  const handleClick = useCallback(() => {
    onClose()
    action.onClick()
  }, [action, onClose])

  return (
    <button
      type="button"
      role="menuitem"
      disabled={action.disabled}
      onClick={handleClick}
      className={`context-menu__item ${action.danger ? 'is-danger' : ''}`}
    >
      {action.label}
    </button>
  )
})

export const ContextMenu = memo(function ContextMenu(props: {
  x: number
  y: number
  actions: ContextMenuAction[]
  onClose: () => void
}) {
  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') props.onClose()
    }
    const closeOnScroll = () => props.onClose()
    window.addEventListener('keydown', closeOnEscape)
    window.addEventListener('scroll', closeOnScroll, true)
    return () => {
      window.removeEventListener('keydown', closeOnEscape)
      window.removeEventListener('scroll', closeOnScroll, true)
    }
  }, [props.onClose])

  const left = Math.max(8, Math.min(props.x, window.innerWidth - 205))
  const top = Math.max(8, Math.min(props.y, window.innerHeight - props.actions.length * 36 - 16))
  const position = {
    '--context-menu-left': `${left}px`,
    '--context-menu-top': `${top}px`,
  } as MenuPosition
  const handleBackdropContextMenu = useCallback((event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault()
    props.onClose()
  }, [props.onClose])

  return (
    <>
      <div className="context-menu__backdrop" onMouseDown={props.onClose} onContextMenu={handleBackdropContextMenu} />
      <div role="menu" className="context-menu" style={position}>
        {props.actions.map(action => (
          <ContextMenuItem key={action.label} action={action} onClose={props.onClose} />
        ))}
      </div>
    </>
  )
})
